/**
 * VideoStreamViewer — full-screen overlay for watching a participant's
 * camera or screen-share stream.
 */
import React, { useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { X, Monitor, Video as VideoIcon } from 'lucide-react';

interface VideoStreamViewerProps {
  stream: MediaStream;
  username: string;
  source: 'camera' | 'screen';
  onClose: () => void;
}

const VideoStreamViewer: React.FC<VideoStreamViewerProps> = ({
  stream,
  username,
  source,
  onClose,
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.srcObject = stream;
    }
  }, [stream]);

  // Close on Escape
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  return ReactDOM.createPortal(
    <div
      className="fixed inset-0 bg-black/95 flex flex-col items-center justify-center z-[300]"
      onClick={onClose}
    >
      {/* Header bar */}
      <div
        className="absolute top-0 left-0 right-0 flex items-center justify-between px-4 py-3 bg-gradient-to-b from-black/60 to-transparent"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-2 text-white/80 text-sm font-medium">
          {source === 'screen'
            ? <Monitor className="w-4 h-4 text-white/60" />
            : <VideoIcon className="w-4 h-4 text-white/60" />}
          <span>
            {username}
            {source === 'screen' ? ' partage son écran' : ' partage sa caméra'}
          </span>
        </div>
        <button
          onClick={onClose}
          className="p-2 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
          title="Fermer"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Video */}
      <video
        ref={videoRef}
        autoPlay
        playsInline
        className="max-w-[92vw] max-h-[85vh] rounded-xl border border-white/10 shadow-2xl object-contain"
        onClick={(e) => e.stopPropagation()}
      />

      {/* Footer hint */}
      <p className="absolute bottom-4 text-xs text-white/30">
        Cliquez en dehors ou appuyez sur Échap pour fermer
      </p>
    </div>,
    document.body,
  );
};

export default VideoStreamViewer;
